/**
 * CAD Command Executor - Safe Execution of AI-Parsed CAD Commands
 * Validates and executes structured commands from the AI chatbot
 */

class CADCommandExecutor {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
    this.commandRegistry = this.buildCommandRegistry();

    console.log('[CADCommandExecutor] Initialized with', Object.keys(this.commandRegistry).length, 'commands');
  }

  buildCommandRegistry() {
    return {
      // ========== Sketch Tools ==========
      'activate_tool': {
        description: 'Activate a sketch drawing tool',
        requiredParams: ['tool'],
        validate: (params) => {
          const validTools = ['point', 'line', 'rectangle', 'circle', 'arc', 'spline', 'ellipse', 'polygon', 'slot'];
          return validTools.includes(params.tool);
        },
        execute: (params) => {
          this.cadViewer.sketchEngine.activateTool(params.tool);
          return `Activated ${params.tool} tool`;
        }
      },

      'start_sketch': {
        description: 'Start a new sketch on a plane',
        requiredParams: ['plane'],
        validate: (params) => ['xy', 'xz', 'yz'].includes(params.plane),
        execute: async (params) => {
          const planeMap = {
            'xy': SketchPlane.XY(),
            'xz': SketchPlane.XZ(),
            'yz': SketchPlane.YZ()
          };
          await this.cadViewer.startSketch(planeMap[params.plane]);
          return `Started sketch on ${params.plane.toUpperCase()} plane`;
        }
      },

      // ========== Autonomous Drawing (AI creates geometry) ==========
      'draw_rectangle': {
        description: 'Draw a rectangle programmatically',
        requiredParams: ['x1', 'y1', 'x2', 'y2'],
        validate: (params) => {
          return typeof params.x1 === 'number' && typeof params.y1 === 'number' &&
                 typeof params.x2 === 'number' && typeof params.y2 === 'number';
        },
        execute: async (params) => {
          // Ensure sketch is active
          if (!this.cadViewer.sketchEngine.activeSketch) {
            await this.cadViewer.startSketch(SketchPlane.XY());
          }

          // Create rectangle entity programmatically
          const entity = {
            type: 'rectangle',
            x1: params.x1,
            y1: params.y1,
            x2: params.x2,
            y2: params.y2,
            construction: false
          };

          this.cadViewer.sketchEngine.entities.push(entity);
          this.cadViewer.sketchEngine.renderEntities();

          return `Drew rectangle from (${params.x1}, ${params.y1}) to (${params.x2}, ${params.y2})`;
        }
      },

      'draw_circle': {
        description: 'Draw a circle programmatically',
        requiredParams: ['cx', 'cy', 'radius'],
        validate: (params) => {
          return typeof params.cx === 'number' && typeof params.cy === 'number' &&
                 typeof params.radius === 'number' && params.radius > 0;
        },
        execute: async (params) => {
          if (!this.cadViewer.sketchEngine.activeSketch) {
            await this.cadViewer.startSketch(SketchPlane.XY());
          }

          const entity = {
            type: 'circle',
            cx: params.cx,
            cy: params.cy,
            radius: params.radius,
            construction: false
          };

          this.cadViewer.sketchEngine.entities.push(entity);
          this.cadViewer.sketchEngine.renderEntities();

          return `Drew circle at (${params.cx}, ${params.cy}) with radius ${params.radius}`;
        }
      },

      'draw_line': {
        description: 'Draw a line programmatically',
        requiredParams: ['x1', 'y1', 'x2', 'y2'],
        validate: (params) => {
          return typeof params.x1 === 'number' && typeof params.y1 === 'number' &&
                 typeof params.x2 === 'number' && typeof params.y2 === 'number';
        },
        execute: async (params) => {
          if (!this.cadViewer.sketchEngine.activeSketch) {
            await this.cadViewer.startSketch(SketchPlane.XY());
          }

          const entity = {
            type: 'line',
            x1: params.x1,
            y1: params.y1,
            x2: params.x2,
            y2: params.y2,
            construction: false
          };

          this.cadViewer.sketchEngine.entities.push(entity);
          this.cadViewer.sketchEngine.renderEntities();

          return `Drew line from (${params.x1}, ${params.y1}) to (${params.x2}, ${params.y2})`;
        }
      },

      'finish_sketch': {
        description: 'Finish the current sketch',
        requiredParams: [],
        validate: () => this.cadViewer.sketchEngine.activeSketch !== null,
        execute: () => {
          this.cadViewer.sketchEngine.finishSketch();
          return 'Sketch completed';
        }
      },

      // ========== Primitives ==========
      'create_primitive': {
        description: 'Create a 3D primitive shape',
        requiredParams: ['shape', 'parameters'],
        validate: (params) => {
          const validShapes = ['box', 'cylinder', 'sphere', 'cone', 'torus', 'wedge'];
          return validShapes.includes(params.shape) && typeof params.parameters === 'object';
        },
        execute: async (params) => {
          await this.cadViewer.createPrimitiveShape(params.shape, params.parameters);
          return `Created ${params.shape} with parameters: ${JSON.stringify(params.parameters)}`;
        }
      },

      // ========== 3D Operations ==========
      'perform_extrude': {
        description: 'Extrude the selected sketch',
        requiredParams: ['distance'],
        validate: (params) => {
          const dist = parseFloat(params.distance);
          return !isNaN(dist) && dist > 0 && dist <= 10000;
        },
        execute: async (params) => {
          const selectedFeature = this.cadViewer.document?.getFeature(this.cadViewer.selectedFeatureId);

          if (!selectedFeature || selectedFeature.type !== 'sketch') {
            throw new Error('No sketch selected. Please select a sketch first.');
          }

          // Perform extrude (this will prompt for distance, but we could make it non-interactive)
          // For now, we'll need to create a non-interactive version
          await this.performExtrudeAI(parseFloat(params.distance), params.direction || 'normal');
          return `Extruded ${params.distance}mm`;
        }
      },

      'perform_revolve': {
        description: 'Revolve the selected sketch',
        requiredParams: ['angle'],
        validate: (params) => {
          const angle = parseFloat(params.angle);
          return !isNaN(angle) && angle > 0 && angle <= 360;
        },
        execute: async (params) => {
          const selectedFeature = this.cadViewer.document?.getFeature(this.cadViewer.selectedFeatureId);

          if (!selectedFeature || selectedFeature.type !== 'sketch') {
            throw new Error('No sketch selected');
          }

          // Would need non-interactive revolve
          throw new Error('Revolve command not yet implemented for AI');
        }
      },

      // ========== Document Operations ==========
      'save_document': {
        description: 'Save the current CAD document',
        requiredParams: [],
        validate: () => this.cadViewer.document !== null,
        execute: async () => {
          const success = await this.cadViewer.saveDocument();
          if (success) {
            return 'Document saved successfully';
          } else {
            throw new Error('Failed to save document');
          }
        }
      },

      // ========== History Operations ==========
      'undo': {
        description: 'Undo the last operation',
        requiredParams: [],
        validate: () => this.cadViewer.historyManager?.canUndo() || false,
        execute: () => {
          const success = this.cadViewer.historyManager.undo();
          if (success) {
            return 'Undid last operation';
          } else {
            throw new Error('Nothing to undo');
          }
        }
      },

      'redo': {
        description: 'Redo the last undone operation',
        requiredParams: [],
        validate: () => this.cadViewer.historyManager?.canRedo() || false,
        execute: () => {
          const success = this.cadViewer.historyManager.redo();
          if (success) {
            return 'Redid operation';
          } else {
            throw new Error('Nothing to redo');
          }
        }
      },

      // ========== Selection Operations ==========
      'select_feature': {
        description: 'Select a feature by name or index',
        requiredParams: ['target'],
        validate: () => true,
        execute: (params) => {
          // Find feature by name or index
          const features = this.cadViewer.document?.features || [];
          let feature = null;

          if (typeof params.target === 'number') {
            feature = features[params.target];
          } else {
            feature = features.find(f => f.name.toLowerCase().includes(params.target.toLowerCase()));
          }

          if (feature) {
            this.cadViewer.selectFeature(feature.id);
            return `Selected feature: ${feature.name}`;
          } else {
            throw new Error(`Feature not found: ${params.target}`);
          }
        }
      },

      'select_sketch': {
        description: 'Select a sketch by index (e.g., "sketch 1", "sketch 2")',
        requiredParams: ['index'],
        validate: (params) => typeof params.index === 'number' && params.index > 0,
        execute: (params) => {
          const sketches = this.cadViewer.document?.features.filter(f => f.type === 'sketch') || [];
          const sketchIndex = params.index - 1;  // Convert to 0-indexed

          if (sketchIndex < 0 || sketchIndex >= sketches.length) {
            throw new Error(`Sketch ${params.index} not found. Available: ${sketches.length} sketches`);
          }

          const sketch = sketches[sketchIndex];
          this.cadViewer.selectFeature(sketch.id);
          return `Selected ${sketch.name} (Sketch ${params.index})`;
        }
      },

      'select_entity': {
        description: 'Select an entity within a sketch (e.g., "rectangle 1 in sketch 1")',
        requiredParams: ['entityType', 'entityIndex', 'sketchIndex'],
        validate: (params) => {
          return typeof params.entityIndex === 'number' &&
                 typeof params.sketchIndex === 'number' &&
                 params.entityIndex > 0 &&
                 params.sketchIndex > 0;
        },
        execute: (params) => {
          const sketches = this.cadViewer.document?.features.filter(f => f.type === 'sketch') || [];
          const sketchIdx = params.sketchIndex - 1;

          if (sketchIdx < 0 || sketchIdx >= sketches.length) {
            throw new Error(`Sketch ${params.sketchIndex} not found`);
          }

          const sketch = sketches[sketchIdx];
          const entities = sketch.parameters?.entities || [];
          const entityIdx = params.entityIndex - 1;

          if (entityIdx < 0 || entityIdx >= entities.length) {
            throw new Error(`Entity ${params.entityIndex} not found in ${sketch.name}`);
          }

          const entity = entities[entityIdx];

          // Verify entity type matches if specified
          if (params.entityType && entity.type !== params.entityType) {
            throw new Error(`Entity ${params.entityIndex} in ${sketch.name} is a ${entity.type}, not a ${params.entityType}`);
          }

          // Select the parent sketch and highlight the entity
          this.cadViewer.selectFeature(sketch.id);

          return `Selected ${entity.type} ${params.entityIndex} in ${sketch.name}`;
        }
      },

      // ========== Query Operations ==========
      'list_features': {
        description: 'List all features in the document',
        requiredParams: [],
        validate: () => true,
        execute: () => {
          const features = this.cadViewer.document?.features || [];
          if (features.length === 0) {
            return 'No features in document';
          }
          const list = features.map((f, i) => `${i + 1}. ${f.name} (${f.type})`).join('\n');
          return `Features:\n${list}`;
        }
      },

      'get_info': {
        description: 'Get information about the current document',
        requiredParams: [],
        validate: () => true,
        execute: () => {
          const doc = this.cadViewer.document;
          if (!doc) return 'No document loaded';

          return `Document: ${doc.name}\nFeatures: ${doc.features.length}\nParts: ${doc.parts.length}\nSketches: ${doc.sketches.length}`;
        }
      }
    };
  }

  async executeCommand(command) {
    console.log('[CADCommandExecutor] Executing:', command);

    // Validate command exists
    const handler = this.commandRegistry[command.action];
    if (!handler) {
      return {
        success: false,
        action: command.action,
        error: `Unknown command: ${command.action}. Available commands: ${Object.keys(this.commandRegistry).join(', ')}`
      };
    }

    // Validate required parameters
    const missingParams = handler.requiredParams.filter(p => !(p in command.params));
    if (missingParams.length > 0) {
      return {
        success: false,
        action: command.action,
        error: `Missing required parameters: ${missingParams.join(', ')}`
      };
    }

    // Validate parameters
    try {
      if (!handler.validate(command.params)) {
        return {
          success: false,
          action: command.action,
          error: `Invalid parameters for ${command.action}`
        };
      }
    } catch (validationError) {
      return {
        success: false,
        action: command.action,
        error: `Validation failed: ${validationError.message}`
      };
    }

    // Execute command
    try {
      const result = await handler.execute(command.params);
      return {
        success: true,
        action: command.action,
        result: result,
        message: result || `Successfully executed ${command.action}`
      };
    } catch (error) {
      console.error('[CADCommandExecutor] Execution error:', error);
      return {
        success: false,
        action: command.action,
        error: error.message || 'Execution failed'
      };
    }
  }

  // ========== Non-Interactive AI Versions of Operations ==========

  async performExtrudeAI(distance, direction = 'normal') {
    const selectedFeature = this.cadViewer.document?.getFeature(this.cadViewer.selectedFeatureId);

    if (!selectedFeature || selectedFeature.type !== 'sketch') {
      throw new Error('No sketch selected for extrude');
    }

    // Create extrude feature
    const feature = new Feature(
      Utils.generateId(),
      'extrude',
      {
        name: `AI Extrude`,
        baseSketch: selectedFeature.id,
        distance: distance,
        direction: direction
      }
    );

    this.cadViewer.document.addFeature(feature);
    this.cadViewer.updateFeatureTree();

    // Convert sketch to shape
    const shape = this.cadViewer.convertSketchToShape(selectedFeature.parameters.entities);
    if (!shape) {
      throw new Error('Could not convert sketch to shape');
    }

    // Create extrude geometry
    const extrudeSettings = {
      depth: distance,
      bevelEnabled: false,
      curveSegments: 32
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const material = new THREE.MeshStandardMaterial({
      color: 0x808080,
      metalness: 0.3,
      roughness: 0.7
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.userData = {
      isCADFeature: true,
      featureId: feature.id,
      featureName: feature.name
    };

    // Create Part from mesh
    const part = this.cadViewer.createPartFromMesh(mesh, feature.id, feature.name);
    this.cadViewer.document.parts.push(part);
    this.cadViewer.parts.set(part.id, part);
    this.cadViewer.document.hasUnsavedChanges = true;

    this.cadViewer.scene.add(mesh);
    this.cadViewer.cadObjects.set(feature.id, mesh);

    // Record in history
    if (this.cadViewer.historyManager) {
      this.cadViewer.historyManager.recordOperation({
        type: 'add_feature',
        name: `AI Extrude ${distance}mm`,
        featureId: feature.id,
        partId: part.id,
        feature: JSON.parse(JSON.stringify(feature)),
        part: JSON.parse(JSON.stringify(part)),
        geometry: this.cadViewer.historyManager._serializeGeometry(mesh)
      });
    }

    return `Extruded ${selectedFeature.name} by ${distance}mm`;
  }

  // List available commands for help
  getAvailableCommands() {
    return Object.entries(this.commandRegistry).map(([name, handler]) => ({
      name,
      description: handler.description,
      requiredParams: handler.requiredParams
    }));
  }
}

// Initialize global instance when CADViewer is ready
if (typeof window !== 'undefined') {
  window.initCADCommandExecutor = function(cadViewer) {
    window.cadCommandExecutor = new CADCommandExecutor(cadViewer);
    console.log('[Global] CADCommandExecutor initialized');
  };
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { CADCommandExecutor };
}
